/**
 * One row in the stack of things a donor can do at an event (GH-781).
 *
 * ## One shape for every offer
 *
 * Check in. Vote. Enter the raffle. Ask something. Write on the wall. Buy a ticket. Six unrelated
 * capabilities, and the owner's design gives all of them the same row — a mark, a short label, and
 * one green thing to press on the trailing edge — because a donor scanning a phone in a hall is
 * not sorting them by kind. They are looking for the next thing to do, and a screen where every
 * offer looks different makes them read six times instead of once.
 *
 * That is also why this is a component rather than six blocks of markup: a seventh offer arrives
 * with the row already decided, and a change to how a row reads happens once.
 *
 * ## The body, and why it is not a second page
 *
 * A card can expand in place. The owner's requirement is literal — *everything on one screen* —
 * and the thing a pushed view would cost is exactly the thing this page exists for: the live
 * total goes off screen the moment the donor acts on a poll. So the prompt renders inside the
 * card, under the row that opened it, with the figure still above.
 *
 * ## What the mark may and may not say
 *
 * The glyph is the *kind* and the dot is the *state*. They are separate because a closed poll
 * would otherwise be identified by nothing but its title, and because a state expressed only as
 * a colour fails greyscale, `forced-colors`, and colour blindness alike (SPEC.md §17.2) — so the
 * dot is decoration on top of a label that already says "Live poll is open" in words.
 */
import type { ReactNode } from "react";

export function EventActionCard({
  glyph,
  label,
  meta,
  live = false,
  action,
  failure,
  children,
}: {
  /** The kind, as an icon. Always `aria-hidden` inside — the label is the accessible name. */
  glyph: ReactNode;
  /**
   * What this row offers, in the organization's own words where it has any: a prompt's row is
   * titled with the prompt's `title`, never with the word "Poll". A subtitle reading "Poll" under
   * a poll is a wasted line, which is the lesson iOS's `PromptRow` already carries.
   */
  label: ReactNode;
  /** Prize, deadline, ticket price — the one extra fact, or nothing. */
  meta?: ReactNode;
  /** Draws the open-now dot. State, on top of a label that already says so in words. */
  live?: boolean;
  /** The trailing control: a button, a link, or a plain span where there is nothing to press. */
  action?: ReactNode;
  /**
   * A refusal that belongs to *this* row — a rate-limited vote, a 403 on an upvote. Rendered
   * beside the card and never as a page-level error: iOS carries three separate failure slots for
   * the same reason, so that a refused reaction cannot be mistaken for the event having broken.
   */
  failure?: string | null;
  /** The expanded body, when this card is the open one. */
  children?: ReactNode;
}) {
  return (
    <li className="actioncard">
      <div className="actioncard__row">
        <span className="actioncard__glyph" aria-hidden="true">
          {live && <span className="actioncard__dot" />}
          {glyph}
        </span>
        <span className="actioncard__text">
          <span className="actioncard__label" dir="auto">
            {label}
          </span>
          {meta && (
            <span className="actioncard__meta" dir="auto">
              {meta}
            </span>
          )}
        </span>
        {action}
      </div>
      {failure && (
        <p className="notice notice--error actioncard__failure" role="alert">
          {failure}
        </p>
      )}
      {children && <div className="actioncard__body">{children}</div>}
    </li>
  );
}
